import { flatten } from 'q-flat';
import queryString from 'query-string';
import { Env } from '@/consts/Env';
import { logoutUser } from '@/libs/auth/logoutUser';
import { checkToken } from '@/redux/slices/userSlice';
import { store } from '@/redux/store';
import { authApi } from './auth/auth.api';
import { tokenManager } from './token-manager';
import { applyTrackingHeaders } from './trackingHeaders';

export const BASE_URL = Env.NEXT_PUBLIC_API_URL;

type RefreshTokens = Awaited<ReturnType<typeof authApi.refreshToken>>;
let refreshInFlight: Promise<RefreshTokens> | null = null;

// Fetch-based token refresh logic
// When a 401 error occurs:
// - Checks if refresh token exists
// - Uses a single-flight pattern to ensure only one refresh request at a time
// - Updates tokens in tokenManager and Redux store after successful refresh
// - Retries the original request with new access token
// - Triggers logout if refresh fails

// Types for request and response
export interface RequestConfig extends RequestInit {
  params?: Record<string, any>;
  baseURL?: string;
  skipAuth?: boolean; // Skip auth for specific requests
  _retry?: boolean; // internal flag to avoid infinite retry loops
}

// Custom error class for API errors
export class APIError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public data?: any,
  ) {
    super(message);
    this.name = 'APIError';
  }
}

// Helper function to build URL with query parameters
function buildURL(url: string, params?: Record<string, any>): string {
  if (!params) {
    return url;
  }

  const flatParams = flatten(params);

  const qs = queryString.stringify(flatParams);

  return qs ? `${url}?${qs}` : url;
}

async function parseResponseBody<T>(response: Response): Promise<T> {
  // Some endpoints return 204 or an empty body (e.g. logout). Avoid throwing on JSON parsing.
  const contentType = response.headers.get('content-type') ?? '';
  const text = await response.text().catch(() => '');

  if (!text) {
    return undefined as T;
  }

  if (contentType.includes('application/json')) {
    return JSON.parse(text) as T;
  }

  // Fallback for non-JSON responses
  return text as unknown as T;
}

async function parseErrorPayload(response: Response): Promise<any> {
  const contentType = response.headers.get('content-type') ?? '';
  const text = await response.text().catch(() => '');

  if (!text) {
    return {};
  }

  if (contentType.includes('application/json')) {
    try {
      return JSON.parse(text);
    } catch {
      return { message: text };
    }
  }

  return { message: text };
}

// Shared by every tab of the origin, so only one of them talks to the refresh endpoint at a time.
const REFRESH_LOCK_NAME = 'auth-token-refresh';
// The refresh request gives up first, so the lock holder releases the lock before other tabs stop waiting.
const REFRESH_REQUEST_TIMEOUT_MS = 10_000;
const REFRESH_LOCK_TIMEOUT_MS = 15_000;

// `AbortSignal.timeout` is missing in older Safari, which already has Web Locks — wait without a limit there.
function createTimeoutSignal(ms: number): AbortSignal | undefined {
  return typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(ms) : undefined;
}

// The backend rotates the refresh token on every refresh and the cookies are shared between tabs,
// so if another tab already rotated the pair, the cookies now hold tokens we haven't sent yet.
function getTokensRotatedByAnotherTab(staleRefreshToken: string | null): RefreshTokens | null {
  const accessToken = tokenManager.getAccessToken();
  const refreshToken = tokenManager.getRefreshToken();

  if (!accessToken || !refreshToken || refreshToken === staleRefreshToken) {
    return null;
  }

  return {
    accessToken,
    refreshToken,
    expiresIn: tokenManager.getTokenExpiry() ?? 0,
    type: 'Bearer',
  };
}

async function refreshTokensAcrossTabs(): Promise<RefreshTokens> {
  const staleRefreshToken = tokenManager.getRefreshToken();

  const refresh = async () => {
    // While we waited for the lock another tab may have finished its refresh — reuse its tokens
    // instead of sending the already-rotated refresh token, which the backend rejects with 401.
    const rotatedTokens = getTokensRotatedByAnotherTab(staleRefreshToken);
    if (rotatedTokens) {
      return rotatedTokens;
    }

    try {
      return await authApi.refreshToken(createTimeoutSignal(REFRESH_REQUEST_TIMEOUT_MS));
    } catch (error) {
      // Browsers without Web Locks can still race: a 401 here may only mean another tab won.
      const tokensFromWinner = getTokensRotatedByAnotherTab(staleRefreshToken);
      if (tokensFromWinner) {
        return tokensFromWinner;
      }
      throw error;
    }
  };

  if (typeof navigator !== 'undefined' && navigator.locks) {
    try {
      return await navigator.locks.request(
        REFRESH_LOCK_NAME,
        { signal: createTimeoutSignal(REFRESH_LOCK_TIMEOUT_MS) },
        refresh,
      );
    } catch (error) {
      // The tab holding the lock is stuck on its refresh — stop waiting and refresh on our own;
      // the 401 recovery in `refresh` still covers the race with that tab.
      if (error instanceof DOMException && error.name === 'TimeoutError') {
        return refresh();
      }
      throw error;
    }
  }

  return refresh();
}

export async function refreshTokensSingleFlight(): Promise<RefreshTokens> {
  if (!refreshInFlight) {
    refreshInFlight = refreshTokensAcrossTabs().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// Base fetcher function
export async function fetcher<T>(
  url: string,
  config: RequestConfig = {},
): Promise<T> {
  try {
    const {
      params,
      baseURL: configBaseURL,
      skipAuth,
      _retry,
      ...requestInit
    } = config;

    // Apply base URL
    const baseURL = configBaseURL || BASE_URL;
    const fullURL = url.startsWith('http') ? url : `${baseURL}${url}`;

    // Build URL with query parameters
    const finalURL = buildURL(fullURL, params);

    // Prepare headers
    const headers = new Headers(requestInit.headers);
    // Default to JSON, but don't force it for FormData uploads.
    if (!(requestInit.body instanceof FormData) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    // Add auth token if not skipped
    let sentAccessToken: string | null = null;
    if (!skipAuth) {
      try {
        const token = tokenManager.getAccessToken();

        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
          sentAccessToken = token;
        }
      } catch {
        // ignore
      }
    }

    try {
      await applyTrackingHeaders({
        set: (name, value) => {
          headers.set(name, value);
        },
        delete: (name) => {
          headers.delete(name);
        },
      });
    } catch (e) {
      console.warn('Failed to apply tracking headers:', e);
    }
    // Prepare final config
    const finalConfig: RequestInit = {
      ...requestInit,
      headers,
      // Match axios `withCredentials: true` behavior (send cookies when allowed)
      credentials: 'include',
    };

    // Make the request
    const response = await fetch(finalURL, finalConfig);

    // Handle non-2xx responses
    if (!response.ok) {
      const data = await parseErrorPayload(response);
      if (response.status === 401 && !skipAuth && !_retry) {
        // Check if refresh token exists before attempting refresh
        const refreshToken = tokenManager.getRefreshToken();
        if (!refreshToken) {
          tokenManager.removeTokens();
          store().dispatch(checkToken());
          throw new APIError(
            response.status,
            data.code || 'UNAUTHORIZED',
            data.message || 'Unauthorized',
            data,
          );
        }

        try {
          // Another tab may have refreshed while this request was in flight — retry with its token
          // instead of rotating the pair once more.
          const currentAccessToken = tokenManager.getAccessToken();
          const newTokens = currentAccessToken && currentAccessToken !== sentAccessToken
            ? { accessToken: currentAccessToken }
            : await refreshTokensSingleFlight();

          store().dispatch(checkToken());

          // Rebuild headers with refreshed access token
          const retryHeaders = new Headers(headers);
          retryHeaders.set('Authorization', `Bearer ${newTokens.accessToken}`);

          // Retry original request once
          const retryConfig: RequestInit = {
            ...finalConfig,
            headers: retryHeaders,
          };

          const retryResponse = await fetch(finalURL, retryConfig);
          if (!retryResponse.ok) {
            const retryData = await parseErrorPayload(retryResponse);
            throw new APIError(
              retryResponse.status,
              retryData.code || 'UNKNOWN_ERROR',
              retryData.message || 'An unknown error occurred',
              retryData,
            );
          }
          return await parseResponseBody<T>(retryResponse);
        } catch (refreshError) {
          // If refresh fails (e.g., refresh token is invalid/expired)
          // Clear tokens and trigger logout
          if (refreshError instanceof APIError && refreshError.status === 401) {
            logoutUser(false, true);
          }
          console.error('Failed to refresh token:', refreshError);
          // Re-throw the original 401 error
          throw new APIError(
            response.status,
            data.code || 'UNAUTHORIZED',
            data.message || 'Unauthorized',
            data,
          );
        }
      }
      throw new APIError(
        response.status,
        data.code || 'UNKNOWN_ERROR',
        data.message || 'An unknown error occurred',
        data,
      );
    }

    return await parseResponseBody<T>(response);
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    if (error instanceof DOMException && error.name === 'TimeoutError') {
      throw new APIError(0, 'NETWORK_ERROR', 'Request timed out');
    }
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new APIError(0, 'NETWORK_ERROR', 'Network error occurred');
    }
    throw new APIError(500, 'INTERNAL_ERROR', 'An internal error occurred');
  }
}

// Convenience methods
export const httpClient = {
  get: <T>(url: string, config?: RequestConfig) =>
    fetcher<T>(url, { ...config, method: 'GET' }),

  post: <T>(url: string, data?: any, config?: RequestConfig) =>
    fetcher<T>(url, {
      ...config,
      method: 'POST',
      body: data
        ? (data instanceof FormData ? data : JSON.stringify(data))
        : undefined,
    }),

  put: <T>(url: string, data?: any, config?: RequestConfig) =>
    fetcher<T>(url, {
      ...config,
      method: 'PUT',
      body: data
        ? (data instanceof FormData ? data : JSON.stringify(data))
        : undefined,
    }),

  patch: <T>(url: string, data?: any, config?: RequestConfig) =>
    fetcher<T>(url, {
      ...config,
      method: 'PATCH',
      body: data
        ? (data instanceof FormData ? data : JSON.stringify(data))
        : undefined,
    }),

  delete: <T>(url: string, config?: RequestConfig) =>
    fetcher<T>(url, { ...config, method: 'DELETE' }),
};
